import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get, debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { eramanBEApi, post } from "~/Services";
import { eramanApiURL } from "~/Utils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn } from "~/Utils/TableRenderFunction";
import { useUsersDocumentDetails } from "./Users.hook";

export const Users = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>("createdAt");
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { openToast } = useNotification();

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useUsersDocumentDetails(id || "");

  console.log("Users-hook", data, selectedRowData, drilldown);

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const getRecords = useCallback(
    (sortOrderParam: string, orderByParam: string, searchStringParam: string, currentPageParam: number) => {
      setIsLoading(true);
      let url = `/admin/users?pageNo=${currentPageParam}&pageSize=${10}&sortBy=${orderByParam ?? ""}&sortOrder=${sortOrderParam ?? ""}&search=${searchStringParam ?? ""}`;

      eramanBEApi
        .get(url)
        .then((res: any) => {
          if (res && res.data) {
            setTotalRecords(res.data.totalRows);
            setData(res.data.data);
          }
        })
        .catch((err) => {
          setData([]);
          console.error("Error fetching data:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, currentPage);
  }, [getRecords, sortOrder, orderByName, searchString, currentPage]);

  const columns: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "userName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "userName"
            ? "descend"
            : undefined,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name"
            ? "descend"
            : undefined,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "email"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "email"
            ? "descend"
            : undefined,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "role"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "role"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    // },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => {
        console.log("record", record);
        return renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/eraman/configuration-users/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        );
      },
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    // setCurrentPage(1);
    // getRecords(order, column.dataIndex, searchString, currentPage);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
    setCurrentPage(1);
  }, 1000);

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Configuration",
      subModule: "users",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteUserRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${eramanApiURL}/admin/users?id=${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col: any) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        className="table-clickable"
        tableData={
          data
            ? data?.map((d: any) => {
                return {
                  ...d,
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        multiSelect={false}
        hideExport={true}
        isServerSearchApi={true}
        searchPlaceholderText={"Search"}
        onSearchChange={handleSearchChange}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage}
          total={totalRecords}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "users") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteUserRequest}
      />
    </>
  );
};
